import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getPhotosByCategory } from "../services/photoService";
import { FILE_BASE_URL } from "../utils/constants";

export default function PhotoGallery() {
  const { id } = useParams();
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const SWIPE_THRESHOLD = 40;

  // Build proper image URL
  const buildFileUrl = (url) =>
    url?.startsWith("http")
      ? url
      : `${FILE_BASE_URL.replace(/\/$/, "")}/${String(url || "").replace(
          /^\//,
          ""
        )}`;

  // Load photos
  useEffect(() => {
    let isMounted = true;

    const loadPhotos = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const data = await getPhotosByCategory(id);
        if (isMounted) setPhotos(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load gallery photos:", error);
        if (isMounted) {
          setPhotos([]);
          setLoadError("Failed to load photos. Please refresh.");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadPhotos();
    return () => {
      isMounted = false;
    };
  }, [id]);

  const selectedIndex = selectedPhoto
    ? photos.findIndex((photo) => photo.id === selectedPhoto.id)
    : -1;

  const showPreviousPhoto = () => {
    if (selectedIndex <= 0) return;
    setSelectedPhoto(photos[selectedIndex - 1]);
  };

  const showNextPhoto = () => {
    if (selectedIndex === -1 || selectedIndex >= photos.length - 1) return;
    setSelectedPhoto(photos[selectedIndex + 1]);
  };

  // Touch swipe support
  const handleTouchStart = (event) => {
    const x = event.changedTouches?.[0]?.clientX;
    setTouchStartX(typeof x === "number" ? x : null);
    setTouchEndX(null);
  };

  const handleTouchMove = (event) => {
    const x = event.changedTouches?.[0]?.clientX;
    if (typeof x === "number") setTouchEndX(x);
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return;
    const deltaX = touchEndX - touchStartX;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
    if (deltaX < 0) showNextPhoto();
    if (deltaX > 0) showPreviousPhoto();
  };

  // Keyboard support
  useEffect(() => {
    if (!selectedPhoto) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setSelectedPhoto(null);
      if (event.key === "ArrowLeft") showPreviousPhoto();
      if (event.key === "ArrowRight") showNextPhoto();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedPhoto, selectedIndex, photos]);

  return (
    <div className="container mt-5 mb-5">
      <h3 className="text-center mb-4 fw-bold">Photo Gallery</h3>

      {/* Loading */}
      {isLoading && (
        <div
          className="d-flex flex-column align-items-center justify-content-center bg-white border rounded-4 shadow-sm"
          style={{ minHeight: "300px" }}
        >
          <div className="spinner-border text-primary"></div>
          <p className="text-muted mt-3 mb-0">Loading photos...</p>
        </div>
      )}

      {/* Error */}
      {!isLoading && loadError && (
        <div className="alert alert-warning text-center">{loadError}</div>
      )}

      {/* Gallery Grid */}
      {!isLoading && !loadError && (
        <div className="row">
          {photos.map((p) => (
            <div className="col-md-3 col-sm-6 mb-4" key={p.id}>
              <div
                className="card shadow-lg border-0"
                style={{
                  borderRadius: "12px",
                  transition: "transform 0.3s ease",
                  cursor: "pointer",
                }}
                onClick={() => setSelectedPhoto(p)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "scale(1.05)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              >
                <img
                  src={buildFileUrl(p.photoUrl)}
                  alt={p.title}
                  className="card-img-top"
                  style={{
                    height: "200px",
                    objectFit: "cover",
                    borderTopLeftRadius: "12px",
                    borderTopRightRadius: "12px",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Photos */}
      {!isLoading && !loadError && photos.length === 0 && (
        <div className="text-center text-muted py-5">
          No photos found in this category.
        </div>
      )}

      {/* Modal */}
      {selectedPhoto && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.8)",
            zIndex: 1050,
          }}
          onClick={() => setSelectedPhoto(null)}
        >
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "100vh" }}
          >
            <div
              className="card bg-dark border-0 p-3"
              style={{
                maxWidth: "80%",
                maxHeight: "90%",
                position: "relative",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close */}
              <button
                className="btn btn-light position-absolute top-0 end-0 m-2"
                onClick={() => setSelectedPhoto(null)}
              >
                &times;
              </button>

              {/* Previous */}
              <button
                className="btn btn-light position-absolute top-50 start-0 translate-middle-y ms-2 rounded-circle"
                style={{ width: "38px", height: "38px" }}
                onClick={showPreviousPhoto}
                disabled={selectedIndex <= 0}
              >
                ‹
              </button>

              {/* Next */}
              <button
                className="btn btn-light position-absolute top-50 end-0 translate-middle-y me-2 rounded-circle"
                style={{ width: "38px", height: "38px" }}
                onClick={showNextPhoto}
                disabled={
                  selectedIndex === -1 ||
                  selectedIndex >= photos.length - 1
                }
              >
                ›
              </button>

              <img
                src={buildFileUrl(selectedPhoto.photoUrl)}
                alt={selectedPhoto.title}
                className="img-fluid rounded"
                style={{ objectFit: "contain", maxHeight: "80vh" }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              />

              <div className="text-white text-center mt-3">
                {selectedPhoto.title}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
